import { loc } from '@/__core__/localization/localization'

export const getCountryFlags = (isDoubles, players, country) => {
  if (isDoubles) {
    return [players?.[0]?.country?.logo?.small || '', players?.[1]?.country?.logo?.small || '']
  } else {
    return [country?.logo?.small || '']
  }
}

export function shortNameSingle(fullName) {
  const nameParts = fullName.trim().split(' ') // Split the name by spaces
  if (nameParts.length < 2) return fullName // Return as-is if there's no last name

  const firstName = nameParts[0]
  const lastName = nameParts[nameParts.length - 1]
  return `${firstName.charAt(0).toUpperCase()}. ${lastName}`
}

export function getShortNameDoubles(fullName) {
  // Split the full name by " / " if there are multiple players
  const players = fullName.split(' / ')

  // Map through each player to extract the correct last name
  return players
    .map(player => {
      const nameParts = player.split(' ') // Split by space
      const lastName = nameParts.slice(0, nameParts.length - 1).join(' ') // Join everything except the last part (initial)
      return lastName
    })
    .join(' / ')
}

export function generateRows(homeStats, awayStats, languageIdentifier) {
  // Define the mapping of labels to statistics keys
  const statMapping = [
    { label: 'sm_aces', key: 'aces' },
    { label: 'sm_break_points_won', key: 'breakPointsWon' },
    { label: 'sm_max_games_in_a_row', key: 'maxGamesInARow' },
    { label: 'sm_max_points_in_a_row', key: 'maxPointsInARow' }
  ]

  // Generate rows based on mapping
  return statMapping.map(({ label, key }) => {
    const homeValue = homeStats[key] || 0
    const awayValue = awayStats[key] || 0
    const total = homeValue + awayValue

    return {
      label: loc(label, languageIdentifier),
      leftValue: homeValue,
      rightValue: awayValue,
      leftPercentage: total > 0 ? Math.round((homeValue / total) * 100) : 0,
      rightPercentage: total > 0 ? Math.round((awayValue / total) * 100) : 0
    }
  })
}

export function generateServeRows(homeStats, awayStats, languageIdentifier) {
  // Utility function to calculate percentage
  function calculatePercentage(successful, total) {
    return total > 0 ? `${Math.round((successful / total) * 100)}%` : '0%'
  }

  const doesVolleyExist = 'volleyWinners' in homeStats && 'volleyWinners' in awayStats
  let netPointsWonStat = []

  if (doesVolleyExist) {
    netPointsWonStat = [
      {
        leftValue: `${homeStats.volleyWinners || 0}`,
        label: loc('sm_net_points_won', languageIdentifier),
        rightValue: `${awayStats.volleyWinners || 0}`
      }
    ]
  }

  // Generate the rows
  return [
    {
      leftValue: calculatePercentage(homeStats.firstServeSuccessful, homeStats.firstServeSuccessful + homeStats.firstServePointsWon),
      label: loc('sm_1st_serve_in', languageIdentifier),
      rightValue: calculatePercentage(awayStats.firstServeSuccessful, awayStats.firstServeSuccessful + awayStats.firstServePointsWon)
    },
    {
      leftValue: calculatePercentage(homeStats.secondServeSuccessful, homeStats.secondServeSuccessful + homeStats.secondServePointsWon),
      label: loc('sm_2nd_serve_in', languageIdentifier),
      rightValue: calculatePercentage(awayStats.secondServeSuccessful, awayStats.secondServeSuccessful + awayStats.secondServePointsWon)
    },
    {
      leftValue: `${homeStats.doubleFaults || 0}`,
      label: loc('sm_double_faults', languageIdentifier),
      rightValue: `${awayStats.doubleFaults || 0}`
    },
    ...netPointsWonStat
  ]
}

export function calculateTotalPoints(homeStats, awayStats) {
  const homePointsWon = isNaN(homeStats.pointsWon) ? 0 : homeStats.pointsWon
  const awayPointsWon = isNaN(awayStats.pointsWon) ? 0 : awayStats.pointsWon
  const total = homePointsWon + awayPointsWon

  // Handle case where total is 0 to avoid division by zero
  if (total === 0) {
    return {
      total: 0,
      home: '0/0',
      away: '0/0',
      homePointsWon,
      awayPointsWon
    }
  }

  return {
    total,
    home: `${homePointsWon}/${total}`,
    away: `${awayPointsWon}/${total}`,
    homePointsWon,
    awayPointsWon
  }
}

export const formatTime = startTime => {
  return startTime.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false // Specify 24-hour time format
  })
}

export const shouldScheduleRefresh = (isGameLive, startTime, played) => {
  if (isGameLive) return true
  if (played) return false

  const currentTime = Date.now()
  const eventStartTime = new Date(startTime).getTime()

  const timeOffset = 30 * 60 * 1000 // 30 min before the event starts, start data refresh

  return Math.abs(eventStartTime - currentTime) <= timeOffset || Date.now() > new Date(startTime).getTime()
}
